import { useLayoutEffect, useRef, type FocusEvent, type KeyboardEvent } from 'react';
import { Box } from '@mui/material';
import { timecodeFrameToSeconds } from 'weave-timeline';
import { color } from '@/colors';
import { formatFrames, parseFrames } from '@/utils/mediaPlaying';
import { clampValue } from '@/utils/numbers';

const frameInputSx = {
  width: 74,
  height: 24,
  py: 0.5,
  px: 1,
  border: `1px solid ${color.White16_T}`,
  borderRadius: '4px',
  background: 'transparent',
  color: color.White64_T,
  fontFamily: '"DM Mono"',
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: 1,
  outline: 'none',
  boxSizing: 'border-box',
  '&:focus': {
    borderColor: color.White40_T,
    color: color.White100,
  },
  '&::selection': {
    background: color.White16_T,
  },
};

interface FrameInputProps {
  currentTime: number;
  duration: number;
  fps: number;
  paused: boolean;
  onTogglePlay: () => void;
  onSetTime: (time: number, ongoing: boolean) => void;
}

export function FrameInput({ currentTime, duration, fps, paused, onTogglePlay, onSetTime }: FrameInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const syncedValue = formatFrames(currentTime, fps);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (input && document.activeElement !== input) {
      input.value = syncedValue;
    }
  }, [syncedValue]);

  const commit = (value: string): string | null => {
    const frame = parseFrames(value);
    if (frame === null) return null;
    const nextTime = clampValue(timecodeFrameToSeconds(frame, fps), 0, duration);
    onSetTime(nextTime, false);
    return formatFrames(nextTime, fps);
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    event.currentTarget.select();
    if (!paused) onTogglePlay();
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    if (value === syncedValue) return;
    event.currentTarget.value = commit(value) ?? syncedValue;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.currentTarget.value = syncedValue;
      event.currentTarget.blur();
    }
  };

  return (
    <Box
      component="input"
      ref={inputRef}
      sx={frameInputSx}
      defaultValue={syncedValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      data-1p-ignore="true"
      data-lpignore="true"
      data-bwignore="true"
      data-form-type="other"
    />
  );
}
